import {PropsWithChildren} from "react";
import {Alert, Button, Skeleton} from "antd";
import {Link} from "react-router-dom";
import {useOfferingSchedules} from "../hooks/useOfferingSchedules";

/**
 * Withholds an offering form until the coach's schedules are known.
 *
 * The form's `availabilityScheduleId` default is derived from that list, and
 * react-hook-form reads `defaultValues` once at mount. Rendering the form first and
 * filling the value in afterwards would either submit an empty schedule or need a
 * `reset()` that races whatever the coach is typing in the other sections — so the form
 * is mounted exactly once, with complete defaults.
 */
export function OfferingSchedulesGate({children}: PropsWithChildren) {
  const {isLoading, isError, errorMessage, hasNoSchedules} = useOfferingSchedules();

  if (isLoading) {
    return <Skeleton active title={false} paragraph={{rows: 8}} />;
  }

  // "Select a schedule" would be a lie here — there is nothing to select and retrying is
  // the only useful action, so the three states get three different messages.
  if (isError) {
    return (
      <Alert
        type="error"
        showIcon
        message="We couldn't load your schedules"
        description={
          errorMessage ??
          "Something went wrong while loading your availability. Please refresh the page and try again."
        }
      />
    );
  }

  if (hasNoSchedules) {
    return (
      <Alert
        type="warning"
        showIcon
        message="Create a schedule first"
        description="Bookings need a schedule that says when you're available. Set one up and come back to finish this offering."
        action={
          <Link to="/calls/availability">
            <Button size="small" type="primary">
              Set availability
            </Button>
          </Link>
        }
      />
    );
  }

  return <>{children}</>;
}
